import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { COMPANY_STYLES } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Sparkles, AlertTriangle, CalendarClock } from "lucide-react";

const STATUS_DOT = { Assigned: "bg-primary", "In Progress": "bg-warning", Completed: "bg-success", Closed: "bg-muted-foreground" };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Calendar() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [weekOff, setWeekOff] = useState(0);
  const [view, setView] = useState("month");
  const [company, setCompany] = useState("all");
  const [team, setTeam] = useState("all");
  const [data, setData] = useState({ jobs: [], unscheduled: [], teams: [] });
  const [companies, setCompanies] = useState([]);
  const [insights, setInsights] = useState(null);
  const [booking, setBooking] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [bform, setBform] = useState({ scheduled_date: "", start_time: "09:00", duration_minutes: 120, team_leader_id: "" });
  const [pref, setPref] = useState("");
  const [sug, setSug] = useState([]);
  const [sugBusy, setSugBusy] = useState(false);

  const load = useCallback(() => {
    const params = { month };
    if (company !== "all") params.company = company;
    if (team !== "all") params.team_leader_id = team;
    api.get("/calendar", { params }).then((r) => setData(r.data));
    api.get("/schedule/insights").then((r) => setInsights(r.data)).catch(() => {});
  }, [month, company, team]);
  useEffect(() => { load(); api.get("/companies").then((r) => setCompanies(r.data)); }, [load]);

  const days = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const first = new Date(y, m - 1, 1);
    const start = new Date(first);
    start.setDate(1 - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);

  const weekDays = useMemo(() => {
    const base = new Date();
    base.setDate(base.getDate() - ((base.getDay() + 6) % 7) + weekOff * 7);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(base); d.setDate(base.getDate() + i); return d; });
  }, [weekOff]);

  const byDay = useMemo(() => {
    const m = {};
    for (const j of data.jobs) (m[j.scheduled_date] = m[j.scheduled_date] || []).push(j);
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.scheduled_start_time || "").localeCompare(b.scheduled_start_time || "")));
    return m;
  }, [data.jobs]);

  const agendaJobs = useMemo(
    () => data.jobs.slice().sort((a, b) => (a.scheduled_date + (a.scheduled_start_time || "")).localeCompare(b.scheduled_date + (b.scheduled_start_time || ""))),
    [data.jobs]);

  const shiftMonth = (n) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(iso(new Date(y, m - 1 + n, 1)).slice(0, 7));
  };

  const openBooking = (j, date = null) => {
    setBooking(j); setConflict(null); setSug([]); setPref("");
    setBform({ scheduled_date: date || j.scheduled_date || "", start_time: j.scheduled_start_time || "09:00", duration_minutes: j.duration_minutes || 120, team_leader_id: j.team_leader_id || "" });
    suggestFor(j, "");
  };

  const suggestFor = async (j, p) => {
    setSugBusy(true);
    try {
      const r = await api.get("/schedule/suggest", { params: { company: j.company, service: j.service, lat: j.lat ?? undefined, lng: j.lng ?? undefined, preference: p, duration_minutes: j.duration_minutes || 120 } });
      setSug(r.data.suggestions);
      if (!r.data.suggestions.length) toast.info("No open slots in the next 14 days");
    } catch { toast.error("AI suggestions failed"); }
    setSugBusy(false);
  };

  const submitBooking = async () => {
    try {
      const isResched = !!booking.scheduled_date;
      await api.post(`/jobs/${booking.id}/${isResched ? "reschedule" : "book"}`, {
        ...bform, duration_minutes: parseInt(bform.duration_minutes) || 120, team_leader_id: bform.team_leader_id || null,
      });
      toast.success(isResched ? "Rescheduled — customer & team notified" : "Slot booked — team notified");
      setBooking(null); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof d === "object") {
        setConflict(d);
        toast.error("Slot conflict — AI alternatives below");
      } else toast.error(typeof d === "string" ? d : "Booking failed");
    }
  };

  const JobChip = ({ j, small }) => (
    <button draggable data-testid={`cal-job-${j.id}`}
      onDragStart={(e) => e.dataTransfer.setData("jobId", j.id)}
      onClick={() => openBooking(j)}
      title={`${j.scheduled_start_time || ""} ${j.service} — ${j.team_leader_name || "unassigned"}${j.booking_status ? ` · ${j.booking_status}` : ""}`}
      className={`w-full text-left rounded-md border px-1.5 py-1 text-[11px] leading-tight truncate transition-transform hover:scale-[1.02] ${COMPANY_STYLES[j.company] || "bg-primary-tint"}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${STATUS_DOT[j.status] || "bg-muted"}`} />
      {!small && j.scheduled_start_time && <b className="font-mono mr-1">{j.scheduled_start_time}</b>}
      {j.customer_name}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Booking Calendar</h1>
          <p className="text-sm text-muted-foreground">Real-time slots across all brands — click any booking to reschedule</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="w-40 h-9" data-testid="cal-company-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brands</SelectItem>
              {companies.map((c) => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={team} onValueChange={setTeam}>
            <SelectTrigger className="w-40 h-9" data-testid="cal-team-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {data.teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-md border overflow-hidden" data-testid="cal-view-toggle">
            {[["month", "Month"], ["teams", "Teams"], ["agenda", "Agenda"]].map(([v, l]) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                className={`text-xs px-3 py-2 transition-colors ${view === v ? "bg-primary text-inverse" : "bg-card hover:bg-accent"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_300px] gap-4 items-start">
        <Card className="p-4">
          {view === "month" && (
            <>
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="sm" data-testid="cal-prev" onClick={() => shiftMonth(-1)}><ChevronLeft size={16} /></Button>
                <div className="font-heading font-bold" data-testid="cal-month-label">{new Date(month + "-02").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
                <Button variant="ghost" size="sm" data-testid="cal-next" onClick={() => shiftMonth(1)}><ChevronRight size={16} /></Button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
                {days.map((d) => {
                  const key = iso(d);
                  const inMonth = key.startsWith(month);
                  const isToday = key === iso(new Date());
                  return (
                    <div key={key} data-testid={`cal-day-${key}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { const jid = e.dataTransfer.getData("jobId"); const j = data.jobs.find((x) => x.id === jid); if (j) openBooking(j, key); }}
                      className={`min-h-[86px] rounded-md border p-1 space-y-1 transition-colors ${inMonth ? "bg-card" : "bg-muted/40 text-muted-foreground"} ${isToday ? "ring-2 ring-blue-500" : ""}`}>
                      <div className={`text-[11px] font-semibold px-0.5 ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
                      {(byDay[key] || []).slice(0, 3).map((j) => <JobChip key={j.id} j={j} />)}
                      {(byDay[key] || []).length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{byDay[key].length - 3} more</div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {view === "teams" && (
            <>
              <div className="flex items-center justify-between mb-3">
                <Button variant="ghost" size="sm" data-testid="week-prev" onClick={() => setWeekOff(weekOff - 1)}><ChevronLeft size={16} /></Button>
                <div className="font-heading font-bold text-sm">Week of {weekDays[0].toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>
                <Button variant="ghost" size="sm" data-testid="week-next" onClick={() => setWeekOff(weekOff + 1)}><ChevronRight size={16} /></Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[700px]" data-testid="teams-grid">
                  <thead>
                    <tr className="text-left text-muted-foreground uppercase tracking-wide">
                      <th className="p-2 w-32">Team</th>
                      {weekDays.map((d) => <th key={iso(d)} className="p-2">{d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" })}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {data.teams.map((t) => (
                      <tr key={t.id} className="border-t" data-testid={`team-row-${t.id}`}>
                        <td className="p-2 font-medium">{t.name}</td>
                        {weekDays.map((d) => {
                          const cell = (byDay[iso(d)] || []).filter((j) => j.team_leader_id === t.id);
                          return (
                            <td key={iso(d)} className="p-1 align-top min-w-[110px]">
                              <div className="space-y-1">{cell.map((j) => <JobChip key={j.id} j={j} small />)}</div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {view === "agenda" && (
            <div className="space-y-1.5" data-testid="agenda-list">
              {agendaJobs.map((j) => (
                <button key={j.id} data-testid={`agenda-job-${j.id}`} onClick={() => openBooking(j)}
                  className="w-full flex items-center gap-3 border rounded-md px-3 py-2.5 text-sm hover:bg-accent transition-colors text-left">
                  <span className="font-mono text-xs text-muted-foreground w-28 shrink-0">{j.scheduled_date}<br />{j.scheduled_start_time || "—"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${COMPANY_STYLES[j.company] || ""}`}>{j.company}</span>
                  <span className="flex-1 truncate">{j.service} — {j.customer_name}</span>
                  <span className="text-xs text-muted-foreground">{j.team_leader_name || "unassigned"}</span>
                </button>
              ))}
              {!data.jobs.length && <div className="text-sm text-muted-foreground text-center py-10">No bookings this month</div>}
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-4 border-primary bg-primary" data-testid="scheduling-insights-card">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-3"><Sparkles size={15} className="text-primary" /> AI Scheduling Insights</h3>
            {insights ? (
              <div className="space-y-3 text-xs">
                {insights.overbooked.length > 0 && (
                  <div className="flex gap-2"><AlertTriangle size={14} className="text-danger-foreground shrink-0 mt-0.5" />
                    <div><b className="text-danger-foreground">Overbooked this week:</b> {insights.overbooked.map((o) => `${o.name} (${o.jobs} jobs)`).join(", ")}</div></div>
                )}
                {insights.underutilized.length > 0 && (
                  <div className="flex gap-2"><AlertTriangle size={14} className="text-warning-foreground shrink-0 mt-0.5" />
                    <div><b className="text-warning-foreground">Underutilized:</b> {insights.underutilized.map((o) => `${o.name} (${o.jobs} jobs)`).join(", ")}</div></div>
                )}
                {insights.unbooked_leads.length > 0 && (
                  <div><b className="text-primary">SLA-risk leads not yet booked:</b>
                    <ul className="mt-1 space-y-0.5">{insights.unbooked_leads.slice(0, 5).map((l) => <li key={l.id}>• {l.customer_name} ({l.company}, {l.status})</li>)}</ul></div>
                )}
                {!insights.overbooked.length && !insights.underutilized.length && !insights.unbooked_leads.length && (
                  <div className="text-muted-foreground">All teams balanced, no SLA risks.</div>
                )}
              </div>
            ) : <div className="text-xs text-muted-foreground">Loading…</div>}
          </Card>

          <Card className="p-4" data-testid="unscheduled-card">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2 mb-3"><CalendarClock size={15} className="text-primary" /> Awaiting Slot ({data.unscheduled.length})</h3>
            <div className="space-y-2">
              {data.unscheduled.map((j) => (
                <button key={j.id} data-testid={`unscheduled-${j.id}`} onClick={() => openBooking(j)}
                  className="w-full text-left border rounded-md p-2.5 text-xs hover:bg-accent transition-colors">
                  <div className="flex items-center gap-1.5">
                    <span className={`px-1.5 py-0.5 rounded-full border ${COMPANY_STYLES[j.company] || ""}`}>{j.company}</span>
                    <b>{j.customer_name}</b>
                  </div>
                  <div className="text-muted-foreground mt-1">{j.service} · {j.job_no}</div>
                </button>
              ))}
              {!data.unscheduled.length && <div className="text-xs text-muted-foreground">Every job has a slot. Nice.</div>}
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={!!booking} onOpenChange={() => setBooking(null)}>
        <DialogContent className="max-w-lg" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{booking?.scheduled_date ? "Reschedule" : "Book"} — {booking?.job_no}</DialogTitle>
          </DialogHeader>
          {booking && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">{booking.customer_name} · {booking.service} · <b className="text-foreground">{booking.company}</b></div>

              {conflict && (
                <div className="border border-danger bg-danger rounded-md p-3 text-sm" data-testid="conflict-warning">
                  <div className="font-semibold text-danger-foreground flex items-center gap-1.5"><AlertTriangle size={14} /> {conflict.message}</div>
                  <ul className="mt-1.5 text-xs text-danger-foreground space-y-0.5">
                    {conflict.conflicts.map((c) => <li key={`${c.job_no}-${c.time}`}>• {c.job_no} — {c.customer_name} at {c.time} ({c.team_leader_name})</li>)}
                    {conflict.cap_hit && <li>• Daily job cap reached for this team</li>}
                  </ul>
                </div>
              )}

              <div className="border border-primary bg-primary rounded-md p-3 space-y-2" data-testid="ai-slots-panel">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-primary" />
                  <span className="text-sm font-medium">AI Suggested Slots</span>
                </div>
                <div className="flex gap-2">
                  <Input data-testid="cal-pref-input" placeholder='Preference, e.g. "prefers mornings" or "weekend"' value={pref} onChange={(e) => setPref(e.target.value)} className="h-9 text-sm" />
                  <Button size="sm" variant="outline" data-testid="cal-suggest-button" onClick={() => suggestFor(booking, pref)} disabled={sugBusy} className="h-9 shrink-0">{sugBusy ? "Thinking…" : "Suggest"}</Button>
                </div>
                <div className="space-y-1.5">
                  {sug.map((s, i) => (
                    <button key={`${s.date}-${s.start_time}-${s.team_leader_id}`} type="button" data-testid={`cal-slot-${i}`}
                      onClick={() => setBform({ ...bform, scheduled_date: s.date, start_time: s.start_time, duration_minutes: s.duration_minutes, team_leader_id: s.team_leader_id })}
                      className="w-full text-left text-xs border rounded-md px-3 py-2 hover:border-primary hover:bg-primary-hover transition-colors">
                      <b>{s.date}</b> · {s.start_time}–{s.end_time} · {s.team_leader_name}
                      <div className="text-muted-foreground">{s.reason}</div>
                    </button>
                  ))}
                  {!sug.length && !sugBusy && <div className="text-xs text-muted-foreground">No suggestions loaded</div>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Date</label>
                  <Input data-testid="book-date-input" type="date" value={bform.scheduled_date} onChange={(e) => setBform({ ...bform, scheduled_date: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Start time</label>
                  <Input data-testid="book-time-input" type="time" value={bform.start_time} onChange={(e) => setBform({ ...bform, start_time: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Duration (min)</label>
                  <Input data-testid="book-duration-input" type="number" min="30" step="30" value={bform.duration_minutes} onChange={(e) => setBform({ ...bform, duration_minutes: e.target.value })} className="mt-1" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">Team leader</label>
                  <Select value={bform.team_leader_id} onValueChange={(v) => setBform({ ...bform, team_leader_id: v })}>
                    <SelectTrigger data-testid="book-team-select" className="mt-1"><SelectValue placeholder="Keep current" /></SelectTrigger>
                    <SelectContent>{data.teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <Button data-testid="book-confirm-button" onClick={submitBooking} disabled={!bform.scheduled_date || !bform.start_time} className="w-full bg-primary hover:bg-primary-hover">
                {booking.scheduled_date ? "Confirm Reschedule" : "Confirm Booking"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
